import { useMemo, useState } from "react";
import { Eye } from "lucide-react";
import MaterialTable from "material-table";
import { materialTableES } from "../../../../services/materialTableES";
import { Badge } from "../../../../components/Badge";
import { formatNumberToPrice } from "../../../../helpers/priceFormatting";
import styles from "./ProductsTable.module.scss";
import { ProductDetails } from "../ProductDetails/ProductDetails";
import { catalogProducts } from "../../data/stoneflexCatalog";
import useCompanyStore from "../../../../Store/supplierCompany/companyData";
import { useGetAllProducts } from "../../../../utils/react-query/queryEcommerce/queries";
import { type SalesProduct } from "./types";

const USE_BACKEND_INVENTORY = false;

export type ProductStatus = "in-stock" | "low-stock" | "out-of-stock";

export const getProductStatus = (unitsAvailable: number): ProductStatus => {
  if (unitsAvailable === 0) return "out-of-stock";
  if (unitsAvailable < 10) return "low-stock";
  return "in-stock";
};

const getStatusBadge = (status: ProductStatus) => {
  switch (status) {
    case "in-stock":
      return { variant: "success" as const, label: "En Stock" };
    case "low-stock":
      return { variant: "warning" as const, label: "Stock Bajo" };
    case "out-of-stock":
      return { variant: "danger" as const, label: "Agotado" };
  }
};

interface ProductsTableProps {
  searchQuery: string;
  filters: any;
}

export function ProductsTable({ searchQuery, filters }: ProductsTableProps) {
  const { companyName } = useCompanyStore();
  const { data: productsData, isLoading: isLoadingBackend } = useGetAllProducts(
    companyName || "",
    USE_BACKEND_INVENTORY
  );

  const [selectedProduct, setSelectedProduct] = useState<SalesProduct | null>(
    null
  );
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const catalogData = useMemo<SalesProduct[]>(() => {
    return catalogProducts.map((product) => {
      const unitsAvailable =
        (product.warehouseStock || 0) + (product.freeZoneStock || 0);

      return {
        ...product,
        units_available: unitsAvailable,
        status: getProductStatus(unitsAvailable),
      };
    });
  }, []);

  const backendData = useMemo<SalesProduct[]>(() => {
    if (!productsData?.products || !Array.isArray(productsData.products)) {
      return [];
    }

    return productsData.products.map((product: any) => {
      const reference = product.reference || product.name || "";
      const widthCm = Number(product.dimensions?.width ?? product.widthCm ?? 0);
      const lengthCm = Number(
        product.dimensions?.length ?? product.lengthCm ?? 0
      );
      const areaM2 = Number(product.area_m2 ?? product.areaM2 ?? 0);
      const price = Number(product.price ?? 0);
      const warehouseStock = Number(
        product.warehouse_stock ??
          product.warehouseStock ??
          product.units_available ??
          0
      );
      const freeZoneStock = Number(
        product.free_zone_stock ?? product.freeZoneStock ?? 0
      );
      const unitsAvailable = warehouseStock + freeZoneStock;

      return {
        id: String(product.product_id ?? product.id ?? reference),
        reference,
        line: product.line || "",
        widthCm,
        lengthCm,
        areaM2,
        price,
        warehouseStock,
        freeZoneStock,
        imageUrl:
          product.imageUrl ||
          product.principal_image?.[0]?.url ||
          product.principal_image?.[0] ||
          undefined,
        brand: product.brand || "",
        adhesiveName: product.adhesiveName,
        adhesiveUnitsPerSheet: product.adhesiveUnitsPerSheet,
        adhesiveUnitPrice: product.adhesiveUnitPrice,
        sealantName: product.sealantName,
        sealantCoverageM2PerQuart: product.sealantCoverageM2PerQuart,
        sealantQuartPrice: product.sealantQuartPrice,
        units_available: unitsAvailable,
        status: getProductStatus(unitsAvailable),
      };
    });
  }, [productsData]);

  const data = USE_BACKEND_INVENTORY ? backendData : catalogData;

  const filteredData = data.filter((product) => {
    const normalizedSearchQuery = searchQuery.toLowerCase();
    const matchesSearch =
      !searchQuery ||
      product.reference.toLowerCase().includes(normalizedSearchQuery) ||
      product.id.toLowerCase().includes(normalizedSearchQuery) ||
      product.line.toLowerCase().includes(normalizedSearchQuery) ||
      (product.brand || "").toLowerCase().includes(normalizedSearchQuery) ||
      (product.adhesiveName || "")
        .toLowerCase()
        .includes(normalizedSearchQuery) ||
      (product.sealantName || "").toLowerCase().includes(normalizedSearchQuery);

    const matchesStatus = !filters.status || product.status === filters.status;

    const matchesPriceRange = (() => {
      if (!filters?.priceRange) return true;
      const [minPrice, maxPrice] = filters.priceRange.split(",").map(Number);
      if (Number.isNaN(minPrice) || Number.isNaN(maxPrice)) return true;
      return product.price >= minPrice && product.price <= maxPrice;
    })();

    return matchesSearch && matchesStatus && matchesPriceRange;
  });

  return (
    <div className={styles.container}>
      <MaterialTable
        title=""
        columns={[
          {
            title: "Estado",
            field: "status",
            render: (rowData: SalesProduct) => {
              const { variant, label } = getStatusBadge(rowData.status);
              return <Badge variant={variant}>{label}</Badge>;
            },
            cellStyle: {
              whiteSpace: "nowrap",
            },
          },
          {
            title: "Línea",
            field: "line",
            render: (rowData: SalesProduct) => (
              <span className={styles.lineBadge}>{rowData.line}</span>
            ),
            cellStyle: {
              whiteSpace: "nowrap",
            },
          },
          {
            title: "Referencia",
            field: "reference",
            render: (rowData: SalesProduct) => (
              <div>
                <div className={styles.productInfo}>{rowData.reference}</div>
                <div className={styles.productId}>ID: {rowData.id}</div>
              </div>
            ),
          },
          {
            title: "Marca",
            field: "brand",
            render: (rowData: SalesProduct) => (
              <span className={styles.stockText}>{rowData.brand}</span>
            ),
          },
          {
            title: "Bodega",
            field: "warehouseStock",
            type: "numeric",
          },
          {
            title: "Zona franca",
            field: "freeZoneStock",
            type: "numeric",
          },
          {
            title: "Disponibles",
            field: "units_available",
            type: "numeric",
            render: (rowData: SalesProduct) => (
              <span
                className={`${styles.stockText} ${
                  rowData.units_available === 0
                    ? styles.stockZero
                    : rowData.units_available < 10
                    ? styles.stockLow
                    : styles.stockNormal
                }`}
              >
                {rowData.units_available}
              </span>
            ),
          },
          {
            title: "Adhesivo",
            field: "adhesiveName",
            render: (rowData: SalesProduct) => (
              <span className={styles.stockText}>
                {rowData.adhesiveName || "-"}
              </span>
            ),
          },
          {
            title: "Sellante",
            field: "sealantName",
            render: (rowData: SalesProduct) => (
              <span className={styles.stockText}>
                {rowData.sealantName || "-"}
              </span>
            ),
          },
          {
            title: "Precio sin IVA",
            field: "price",
            type: "numeric",
            render: (rowData: SalesProduct) => (
              <span className={styles.stockText}>
                {formatNumberToPrice(rowData.price)}
              </span>
            ),
          },
          {
            title: "Dimensiones",
            field: "dimensiones",
            render: (rowData: SalesProduct) => (
              <div className={styles.dimensions}>
                <span>
                  {rowData.widthCm}cm × {rowData.lengthCm}cm
                </span>
              </div>
            ),
            cellStyle: {
              textAlign: "center",
            },
          },
          {
            title: "Área m²",
            field: "areaM2",
            type: "numeric",
          },
        ]}
        data={filteredData}
        isLoading={USE_BACKEND_INVENTORY ? isLoadingBackend : false}
        actions={[
          {
            icon: () => <Eye className={styles.actionIcon} />,
            tooltip: "Ver detalles",
            onClick: (_event, rowData) => {
              const product = rowData as SalesProduct;
              setSelectedProduct(product);
              setIsDetailsOpen(true);
            },
          },
        ]}
        options={{
          search: false,
          toolbar: false,
          paging: true,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          actionsColumnIndex: -1,
          padding: "dense",
          headerStyle: {
            backgroundColor: "#f4f6f8",
            color: "#637381",
            fontSize: "14px",
            fontWeight: 600,
            height: "3.5rem",
          },
          rowStyle: {
            fontSize: "14px",
            fontWeight: 400,
            color: "#1C252E",
          },
        }}
        localization={materialTableES}
      />

      <ProductDetails
        product={selectedProduct}
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
      />
    </div>
  );
}
